import styled from '@emotion/styled';
import type { ChipProps as MuiChipProps, Theme } from '@mui/material';
import { Chip as MuiChip, Tooltip } from '@mui/material';
import { X } from '@phosphor-icons/react';
import { Text, TextProps } from 'components/text';
import React from 'react';
import { colors } from 'styles/theme/colors';

type ChipColor =
  | 'brand'
  | 'coral'
  | 'grey'
  | 'lavender'
  | 'red'
  | 'yellow'
  | 'blue'
  | 'white'
  | 'green'
  | string;

/**
 * Props for the Chip component
 */
export type ChipProps = Pick<
  MuiChipProps,
  | 'label'
  | 'onDelete'
  | 'id'
  | 'component'
  | 'size'
  | 'sx'
  | 'avatar'
  | 'isBordered'
  | 'isBackgroundFilled'
  | 'disabled'
  | 'clickable'
> & {
  /** The color theme for the chip text and border */
  color?: ChipColor;
  /** The background color theme for the chip */
  background?: ChipColor;
  /** The color for the delete icon */
  deleteIconColor?: ChipColor;
  /** The border color theme for the chip */
  borderColor?: ChipColor;
  /** Click handler for the chip */
  onClick?: (event: React.SyntheticEvent) => void;
  /** Whether the chip can be deleted (shows delete icon) */
  isEditable?: boolean;
  /** Custom render function for the label */
  renderLabel?: (label: MuiChipProps['label']) => React.ReactNode;
  /** Whether to show tooltip on hover */
  isTooltip?: boolean;
  /** Items to display before the label */
  startItems?: React.ReactNode;
  /** Items to display after the label */
  endItems?: React.ReactNode;
  /** Count to display alongside the label */
  count?: React.ReactNode;
};

export const chipBaseColors = {
  brand: {
    color: colors.brand[500],
    background: colors.brand[100],
    borderColor: colors.brand[300],
    deleteIconColor: colors.brand[400],
  },
  coral: {
    color: colors.coral[500],
    background: colors.coral[100],
    borderColor: colors.coral[200],
    deleteIconColor: colors.coral[500],
  },
  grey: {
    color: colors.grey[500],
    background: colors.grey[100],
    borderColor: colors.grey[200],
    deleteIconColor: colors.grey[400],
  },
  lavender: {
    color: colors.lavender[800],
    background: colors.lavender[200],
    borderColor: colors.lavender[300],
    deleteIconColor: colors.lavender[600],
  },
  red: {
    color: colors.error[400],
    background: colors.error[100],
    borderColor: colors.error[300],
    deleteIconColor: colors.error[300],
  },
  yellow: {
    color: colors.warning[500],
    background: colors.warning[100],
    borderColor: colors.warning[300],
    deleteIconColor: colors.warning[300],
  },
  blue: {
    color: colors.marineBlue[500],
    background: colors.marineBlue[100],
    borderColor: colors.marineBlue[300],
    deleteIconColor: colors.marineBlue[400],
  },
  white: {
    color: colors.grey[700],
    background: colors.white,
    borderColor: colors.grey[200],
    deleteIconColor: colors.grey[500],
  },
  green: {
    color: colors.success[600],
    background: colors.success[50],
    borderColor: colors.success[300],
    deleteIconColor: colors.success[600],
  },
};

const bgColors = {
  ...chipBaseColors,
  brand: { ...chipBaseColors.brand, color: colors.brand[700], deleteIconColor: colors.brand[300] },
  lavender: { ...chipBaseColors.lavender, borderColor: colors.lavender[500] },
  coral: { ...chipBaseColors.coral, borderColor: colors.coral[300] },
};

const textVariant = { sm: 'bodyXs', md: 'labelSm', lg: 'labelSm' };

const getColor = (
  color: string,
  background: string,
  borderColor: string,
  deleteIconColor: string,
  isBackgroundFilled: boolean
) => {
  const getValue = (
    source: Record<string, Record<string, string>>,
    primaryKey: string,
    prop: string,
    fallbackKeys: string[] = []
  ): string =>
    source?.[primaryKey]?.[prop] ??
    primaryKey ??
    fallbackKeys?.reduce<string | undefined>((acc, key) => acc ?? source?.[key]?.[prop], undefined);

  // For filled background, use bgColors
  if (isBackgroundFilled) {
    return {
      color: getValue(bgColors, color, 'color'),
      background: getValue(bgColors, background, 'background', [color, 'white']),
      borderColor: getValue(bgColors, borderColor, 'borderColor', [color, 'white']),
      deleteIconColor: getValue(bgColors, deleteIconColor, 'deleteIconColor', [color, 'white']),
    };
  }
  // For outlined chip, use chipBaseColors
  return {
    color: getValue(chipBaseColors, color, 'color'),
    background:
      getValue(chipBaseColors, background, 'background') ??
      background ??
      chipBaseColors?.white?.background,
    borderColor: getValue(chipBaseColors, borderColor, 'borderColor', [color, 'white']),
    deleteIconColor: getValue(chipBaseColors, deleteIconColor, 'deleteIconColor', [color, 'white']),
  };
};

const LabelBox = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: inherit;
  .MuiChip-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .chip-label-start-items,
  .chip-label-end-items {
    gap: inherit;
    display: inherit;
    align-items: inherit;
  }
`;

/**
 * Chip component, part of Delogue design system, for displaying compact information with optional delete functionality.
 *
 * @param label - The text content of the chip
 * @param onDelete - Function called when delete icon is clicked
 * @param component - The root element type (default: 'li')
 * @param color - Color ('brand', 'coral', 'grey', 'lavender', 'red', 'yellow', 'blue', 'white' or any color as string) for text
 * @param background - Background ('brand', 'coral', 'grey', 'lavender', 'red', 'yellow', 'blue', 'white' or any color as string)
 * @param borderColor - Border ('brand', 'coral', 'grey', 'lavender', 'red', 'yellow', 'blue', 'white' or any color as string)
 * @param deleteIconColor - Color('brand', 'coral', 'grey', 'lavender', 'red', 'yellow', 'blue', 'white' or any color as string) for the delete icon
 * @param onClick - Click handler for the entire chip
 * @param isEditable - Whether the chip can be deleted
 * @param isTooltip - Whether to show tooltip on hover
 * @param renderLabel - Custom render function for the label
 * @param avatar - Avatar element
 * @param count - Count to display alongside the label
 * @param endItems - Items to display after the label
 * @param startItems - Items to display before the label
 * @param isBackgroundFilled - Whether to use filled background style
 */
export const Chip = ({
  label,
  onDelete,
  component = 'li',
  color: propsColor,
  deleteIconColor: propsDeleteIconColor,
  background: propsBackground,
  borderColor: propsBorderColor,
  onClick,
  isEditable = true,
  isTooltip = true,
  renderLabel,
  avatar,
  count,
  endItems,
  startItems,
  isBackgroundFilled,
  ...rest
}: ChipProps) => {
  if (!label) return null;

  const variantText = textVariant[rest?.size || 'sm'];
  const { background, borderColor, color, deleteIconColor } = getColor(
    propsColor as string,
    propsBackground as string,
    propsBorderColor as string,
    propsDeleteIconColor as string,
    isBackgroundFilled as boolean
  );

  const baseSx: MuiChipProps['sx'] = {
    ...(rest.size === 'lg' && isBackgroundFilled ? { borderWidth: '1px' } : {}),
    background,
    borderColor,
    '.chip-label-box': { color },
  };
  const chipLabel = (
    <LabelBox className="chip-label-box">
      {startItems && <div className="chip-label-start-items">{startItems}</div>}
      {avatar && <div className="chip-label-avatar">{avatar}</div>}
      <Text
        color="inherit"
        weight="semiBold"
        className="MuiChip-label"
        variant={variantText as TextProps['variant']}
      >
        {renderLabel ? renderLabel(label) : label}
      </Text>
      {count && (
        <Text color="inherit" weight="semiBold" variant={variantText as TextProps['variant']}>
          {count}
        </Text>
      )}
      {endItems && <div className="chip-label-end-items">{endItems}</div>}
    </LabelBox>
  );

  const deleteIcon = (
    <X data-testid="CancelIcon" size={16} color={deleteIconColor} style={{ flexShrink: 0 }} />
  );

  const commonProps = {
    ...rest,
    component,
    onDelete: isEditable ? onDelete : undefined,
    onClick,
    deleteIcon,
    sx: (theme: Theme) => ({
      ...baseSx,
      ...(typeof rest.sx === 'function' ? rest.sx(theme) : rest.sx),
    }),
  };

  return isTooltip ? (
    <Tooltip title={renderLabel ? renderLabel(label) : label}>
      <MuiChip {...commonProps} label={chipLabel} />
    </Tooltip>
  ) : (
    <MuiChip {...commonProps} label={chipLabel} />
  );
};

// Chip size styles
export const chipBorderd = {
  borderWidth: '0.5px',
  borderStyle: 'solid',
  borderColor: colors.grey[200],
  background: colors.white,
};
const chipBase = {
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '4px',
  borderRadius: '6px',
  background: colors.white,
  height: 'unset',
  '& .MuiChip-label': {
    paddingInline: 'unset',
    gap: 'inherit',
    lineHeight: 'normal',
  },
  '& .MuiChip-avatar': {
    marginInline: 'unset',
  },
};

export const chipSm = {
  ...chipBase,
  padding: '2px 8px',
  '&.MuiChip-deletable': { padding: '2px 4px 2px 8px' },
  '& .MuiChip-deleteIcon': { width: '10px', height: '10px', margin: 'unset' },
};

export const chipMd = {
  ...chipBase,
  padding: '2px 8px',
  gap: '5px',
  '&.MuiChip-deletable': { padding: '2px 4px 2px 8px' },
  '& .MuiChip-deleteIcon': { width: '12px', height: '12px', margin: 'unset' },
};

export const chipLg = {
  ...chipBase,
  gap: '6px',
  padding: '4px 8px',
  '&.MuiChip-deletable': { padding: '4px 4px 4px 8px' },
  '& .MuiChip-deleteIcon': { width: '14px', height: '14px', margin: 'unset' },
};
